import {Platform} from 'react-native';
import {ApiConfig} from '../config/ApiConfig';
import {getAccessToken} from '../helpers/storage';

/**
 * Generic cross-entity attachment API (`app/services/entityDocumentsApi.js`).
 *
 * Backs the reusable `EntityDocumentsPanel` — mirrors `workOrderPhotoApi.js`'s
 * raw-`fetch` multipart pattern (the JSON-only `apiFetch` helper in
 * `WorkSheetContext` cannot stream `FormData`).
 *
 * Backend (HappyPOS-backend `src/api/controllers/attachment.controller.js`,
 * hand-mounted ahead of the swagger-tools router because multipart bypasses it):
 *   - POST   /api/attachments/{entityType}/{entityId}                multipart, field `file`
 *   - DELETE /api/attachments/{entityType}/{entityId}/{attachmentId} soft-detach (Document Trash, 14d)
 *
 * `entityType` is one of `lead, customer, order, ticket, restaurant, task,
 * complaint, project, asset, nav_invoice, food`; the panel is wired for `lead`
 * only today, the API itself is entity-agnostic. Every entity is missing a
 * generic GET listing endpoint on the backend — the caller must already have
 * the entity's own embedded documents/attachments array (e.g. `Lead.documents`)
 * to render the initial list.
 *
 * @module services/entityDocumentsApi
 */

const ATTACHMENTS_URL = `${ApiConfig.apiURL}/api/attachments`;

/**
 * Bearer + optional store scope headers. The generic attachment routes 400
 * without a resolvable `x-restaurant-id` (they never fall back to the caller's
 * primary store), so a known restaurant id should always be passed once the
 * management scope has resolved.
 *
 * @param {string} [restaurantId] Active store id, when known.
 * @returns {Promise<Object>} Header map (never includes Content-Type — the
 *   multipart boundary is generated by `fetch`/`FormData` itself).
 */
const authHeaders = async (restaurantId) => {
    const accessToken = await getAccessToken();

    if (!accessToken) {
        const authError = new Error('Authentication required');

        authError.code = 'AUTH_REQUIRED';
        throw authError;
    }

    return {
        Authorization: `Bearer ${accessToken}`,
        Accept: 'application/json',
        ...(restaurantId ? {'x-restaurant-id': String(restaurantId)} : {}),
    };
};

/**
 * Turn a non-2xx response into an `Error` carrying the server's own message
 * and status, so a caller can branch on `err.status` (e.g. 404 → fall back to
 * an entity's embedded documents array).
 *
 * @param {Response} response
 * @returns {Promise<Error>}
 */
const buildErrorFromResponse = async (response) => {
    try {
        const body = await response.json();
        // The attachment controller answers `{ error: message }` (not `message`)
        // on its own thrown errors, alongside the generic `{ message }` shape
        // other backend routes use — check both.
        const message = body?.message || body?.error || body?.data?.message || response.statusText;
        const error = new Error(message || `Request failed with status ${response.status}`);

        error.status = response.status;

        return error;
    } catch (_error) {
        const error = new Error(response.statusText || `Request failed with status ${response.status}`);

        error.status = response.status;

        return error;
    }
};

/**
 * @param {Response} response
 * @returns {Promise<*>} Parsed JSON, or `true` for an empty body.
 */
const parseApiResponse = async (response) => {
    if (!response.ok) {
        throw await buildErrorFromResponse(response);
    }
    if (response.status === 204) {
        return true;
    }

    const text = await response.text();

    if (!text) {
        return true;
    }

    try {
        return JSON.parse(text);
    } catch (_error) {
        return true;
    }
};

/**
 * Build the multipart body for a picked document/photo asset.
 *
 * React Native's fetch accepts the `{uri, name, type}` descriptor; on web (and
 * the Tauri desktop build) that object serializes to the literal string
 * "[object Object]" and the server answers "File is required" — there the
 * asset has to be read into a `Blob` first.
 *
 * @param {{uri: string, file?: File}} asset Picked asset (expo-document-picker
 *   / expo-image-picker shape, or `{uri, file}` on web).
 * @param {string} name Upload file name.
 * @param {string} type Upload MIME type.
 * @returns {Promise<FormData>}
 */
const buildAttachmentFormData = async (asset, name, type) => {
    const form = new FormData();

    if (Platform.OS === 'web') {
        const blob = asset?.file || await (await fetch(asset.uri)).blob();

        form.append('file', blob, name);
    } else {
        form.append('file', {uri: asset.uri, name, type});
    }

    return form;
};

/**
 * Upload one file and attach it to an entity via the generic attachments API.
 *
 * @param {string} entityType Owning entity type (e.g. `'lead'`).
 * @param {string} entityId Mongo `_id` of the owning entity.
 * @param {{uri: string, fileName?: string, name?: string, mimeType?: string,
 *   type?: string, size?: number, file?: File}} asset Picked document/photo asset.
 * @param {{restaurantId?: string}} [options] Active store scope, when known.
 * @returns {Promise<{entityType: string, entityId: string, attachment: object}>}
 *   The persisted attachment subdocument (including its generated `_id`).
 */
export async function uploadEntityAttachment(entityType, entityId, asset, options = {}) {
    if (!entityType || !entityId) {
        throw new Error('Missing entity reference');
    }
    if (!asset?.uri) {
        throw new Error('No file selected');
    }

    const name = asset.fileName || asset.name || `document-${Date.now()}`;
    const type = asset.mimeType || asset.type || 'application/octet-stream';
    const form = await buildAttachmentFormData(asset, name, type);

    const response = await fetch(
        `${ATTACHMENTS_URL}/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}`,
        {
            method: 'POST',
            // No Content-Type: the multipart boundary is generated by fetch itself.
            headers: await authHeaders(options.restaurantId),
            body: form,
        },
    );

    return parseApiResponse(response);
}

/**
 * Detach an attachment from an entity (moved to the centralized Document
 * Trash server-side, restorable for 14 days).
 *
 * @param {string} entityType Owning entity type (e.g. `'lead'`).
 * @param {string} entityId Mongo `_id` of the owning entity.
 * @param {string} attachmentId `_id` of the attachment subdocument to remove.
 * @param {{restaurantId?: string}} [options] Active store scope, when known.
 * @returns {Promise<{deleted: boolean, trashId?: string}>}
 */
export async function deleteEntityAttachment(entityType, entityId, attachmentId, options = {}) {
    if (!entityType || !entityId || !attachmentId) {
        throw new Error('Missing entity/attachment reference');
    }

    const response = await fetch(
        `${ATTACHMENTS_URL}/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}/${encodeURIComponent(attachmentId)}`,
        {
            method: 'DELETE',
            headers: await authHeaders(options.restaurantId),
        },
    );

    return parseApiResponse(response);
}

export default {uploadEntityAttachment, deleteEntityAttachment};
